import { CloudOff, Map as MapIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { useSession, useSessionActions } from "../admin/session.ts";
import { LanguageSelect } from "../components/LanguageSelect.tsx";
import { useLocale } from "../i18n/index.ts";
import { useOnline } from "../pwa/online.ts";
import { AccountPage } from "./AccountPage.tsx";
import { ResetRequestPage, ResetSetPage, SignUpPage, VerifiedPage } from "./auth/AuthPages.tsx";
import { LoginPage } from "./LoginPage.tsx";
import { loginUrl } from "../auth/next.ts";
import { MySuggestionsPage } from "./MySuggestionsPage.tsx";
import {
  EditObjectSuggestionPage,
  EditSpotSuggestionPage,
  SuggestObjectPage,
  SuggestSpotPage,
} from "./SuggestPages.tsx";

/** Pages behind a diver (or any) sign-in: anonymous visitors are sent to the login and come back. */
function RequireUser() {
  const { t } = useTranslation();
  const locale = useLocale();
  const session = useSession();
  const location = useLocation();
  if (session.status === "loading") return <p role="status">{t("common.loading")}</p>;
  if (session.status === "anonymous") {
    return (
      <Navigate
        to={loginUrl(locale, location.pathname + location.search + location.hash)}
        replace
      />
    );
  }
  return <Outlet />;
}

/** The suggestion forms are for divers: moderators and admins edit directly, in the admin area. */
function DiverOnly() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { staff } = useSession();
  if (!staff) return <Outlet />;
  return (
    <div className="grid gap-3" data-testid="staff-direct">
      <h1 className="ds-text-title-1 m-0">{t("diver.staffDirectTitle")}</h1>
      <p className="ds-text-body m-0" role="status">
        {t("diver.staffDirect")}
      </p>
      <div className="adm-actions">
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/admin`}>
          {t("diver.staffDirectLink")}
        </Link>
      </div>
    </div>
  );
}

function DiverHeader() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { user, staff } = useSession();
  const actions = useSessionActions();
  const navigate = useNavigate();
  const [note, setNote] = useState<string | null>(null);
  const link = ({ isActive }: { isActive: boolean }) =>
    `adm-navlink${isActive ? " adm-navlink--active" : ""}`;
  return (
    <header className="adm-header">
      <Link to={`/${locale}/`} className="adm-brand ds-text-headline">
        {t("app.name")}
      </Link>
      {user ? (
        <nav aria-label={t("diver.nav")} className="adm-nav">
          <NavLink end to={`/${locale}/konto`} className={link}>
            {t("diver.nav_account")}
          </NavLink>
          {staff ? (
            <NavLink to={`/${locale}/admin`} className={link}>
              {t("diver.nav_admin")}
            </NavLink>
          ) : (
            <NavLink to={`/${locale}/konto/vorschlaege`} className={link}>
              {t("diver.nav_suggestions")}
            </NavLink>
          )}
        </nav>
      ) : (
        <span />
      )}
      <div className="adm-header__right">
        <LanguageSelect plain />
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/`} aria-label={t("admin.toMap")}>
          <MapIcon aria-hidden="true" strokeWidth={1.75} />
          <span className="adm-hide-sm">{t("admin.toMap")}</span>
        </Link>
        {user ? (
          <>
            <button
              type="button"
              className="ds-btn ds-btn--plain"
              onClick={async () => {
                // signing out is a request: offline it would only look as if it had worked
                if (!navigator.onLine) return setNote(t("diver.signOutOffline"));
                setNote(null);
                try {
                  await actions.signOutAndClear({ strict: true });
                } catch {
                  // still signed in: say why, never look signed out
                  return setNote(
                    navigator.onLine ? t("diver.signOutFailed") : t("diver.signOutOffline"),
                  );
                }
                // like every other sign-out: leave the signed-in pages
                void navigate(`/${locale}/`);
              }}
            >
              {t("diver.signOut")}
            </button>
            {note ? (
              <span className="ds-text-footnote text-label-secondary" role="status">
                {note}
              </span>
            ) : null}
          </>
        ) : null}
      </div>
    </header>
  );
}

/** Header, offline notice and page frame of the diver's own pages. */
export function DiverShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const online = useOnline();
  useEffect(() => {
    document.title = `${t("diver.title")} · ${t("app.name")}`;
  }, [t]);
  return (
    <div className="adm-root">
      <DiverHeader />
      <main className="adm-main">
        {online ? null : (
          <p className="adm-warning" role="alert">
            <CloudOff aria-hidden="true" size={20} strokeWidth={1.75} />
            {t("diver.needsConnection")}
          </p>
        )}
        {children}
      </main>
    </div>
  );
}

/** Everything under `/:lang/login`: sign in, sign up, confirmation landing, password reset. */
export function DiverLoginRoute() {
  const locale = useLocale();
  return (
    <DiverShell>
      <Routes>
        <Route index element={<LoginPage />} />
        <Route path="registrieren" element={<SignUpPage />} />
        <Route path="bestaetigt" element={<VerifiedPage />} />
        <Route path="passwort-vergessen" element={<ResetRequestPage />} />
        <Route path="neues-passwort" element={<ResetSetPage />} />
        <Route path="*" element={<Navigate to={`/${locale}/login`} replace />} />
      </Routes>
    </DiverShell>
  );
}

/** Everything under `/:lang/konto`. */
export function DiverKontoRoute() {
  return (
    <DiverShell>
      <Routes>
        <Route element={<RequireUser />}>
          <Route index element={<AccountPage />} />
          <Route element={<DiverOnly />}>
            <Route path="vorschlaege" element={<MySuggestionsPage />} />
            <Route path="vorschlagen/spot" element={<SuggestSpotPage />} />
            <Route path="vorschlagen/spot/:slug" element={<EditSpotSuggestionPage />} />
            <Route path="vorschlagen/objekt/:slug" element={<SuggestObjectPage />} />
            <Route
              path="vorschlagen/objekt/:slug/:objectId"
              element={<EditObjectSuggestionPage />}
            />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </DiverShell>
  );
}
